import { useCallback, useEffect, useMemo } from "react";

import { type AttributionFilter, FilterMode, type MetadataCloud } from "@doitintl/cmp-models";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider } from "@mui/material";
import { type AlertColor } from "@mui/material/Alert";
import { makeStyles } from "@mui/styles";

import { globalText } from "../../../assets/texts";
import { filterDialogText } from "../../../assets/texts/CloudAnalytics";
import { isAttributionDimension, Positions } from "../../../Pages/CloudAnalytics/utilities";
import { type MetadataOption } from "../../../types";
import { useFullScreen } from "../../../utils/dialog";
import { INCREASED_DIALOG_HEIGHT, LIST_ITEM_HEIGHT, REGULAR_DIALOG_HEIGHT } from "./constants";
import useFilterDialog from "./hooks/useFilterDialog";
import useFilterStore from "./hooks/useFilterState";
import { type OnSave } from "./types";
import { getInverse, getPlural } from "./utils";

export type FilterDialogProps = {
  open: boolean;
  openInRegexView?: boolean;
  cloudProviders?: MetadataCloud[] | null;
  disableEscapeKeyDown?: boolean;
  onCancel?: (selected: MetadataOption[]) => void;
  isOptionDisabled?: (option: string) => boolean;
  onClose: () => void;
  onSave: OnSave;
  selected: MetadataOption;
  filter?: AttributionFilter | null | undefined;
  showExcludeSelection?: boolean;
  alertText?: string;
  alertSeverity?: AlertColor;
};

const useStyles = makeStyles((theme) => ({
  listItem: {
    height: LIST_ITEM_HEIGHT,
    maxHeight: LIST_ITEM_HEIGHT,
    outline: "none",
    "&:focus": {
      outline: "none",
    },
  },
  listItemIcon: {
    minWidth: theme.spacing(3),
  },
  spacer: {
    flexGrow: 1,
  },
}));

const FilterDialog = ({
  open,
  cloudProviders,
  disableEscapeKeyDown,
  filter,
  onCancel,
  isOptionDisabled,
  onClose,
  onSave,
  selected,
  alertText,
  alertSeverity,
}: FilterDialogProps) => {
  const classes = useStyles();
  const initialMode = useMemo(() => {
    if (filter?.mode === FilterMode.REGEXP || selected.mode === FilterMode.REGEXP || selected._regexp) {
      return FilterMode.REGEXP;
    }
    return filter?.mode || selected.mode || FilterMode.IS;
  }, [filter?.mode, selected._regexp, selected.mode]);

  const selectedFilter = {
    ...selected,
    mode: initialMode,
    _inverse: filter?.inverse || selected._inverse,
  };
  const { fullScreen } = useFullScreen();
  const isAttribution = isAttributionDimension(selectedFilter);
  const { mode, resetFields } = useFilterStore();

  const onSaveFilters = useCallback(
    (filtersOrRegex: string[] | string) => {
      onSave(mode, filtersOrRegex, getInverse(mode));
      resetFields();
    },
    [mode, onSave, resetFields]
  );

  const { dialog, disabled, regexDisabled, saveFilterChanges } = useFilterDialog({
    selected: selectedFilter,
    filter,
    onSaveFilters,
    inverse: selectedFilter._inverse,
    cloudProviders,
    isOptionDisabled,
    numOfListItems: alertText ? 7 : 6,
  });

  const { plural } = getPlural(selected);

  const saveFilterChangesAndClose = useCallback(async () => {
    await saveFilterChanges();
    onClose();
  }, [onClose, saveFilterChanges]);

  const onCancelDialog = useCallback(() => {
    // on cancel, remove the field if it has no filter values/regexp selected
    if (
      onCancel &&
      !selected._filter &&
      !selected._regexp &&
      (selected._position === Positions.UNUSED || isAttribution)
    ) {
      onCancel([selected]);
    }
    onClose();
  }, [onCancel, isAttribution, onClose, selected]);

  const handleClose = useCallback(
    (_, reason) => {
      if (reason === "backdropClick") {
        return;
      }

      if (reason === "escapeKeyDown") {
        if (disableEscapeKeyDown) {
          return;
        }

        onCancelDialog();
        return;
      }

      onClose();
    },
    [disableEscapeKeyDown, onCancelDialog, onClose]
  );

  useEffect(
    () => () => {
      resetFields();
    },
    [resetFields]
  );

  return (
    <Dialog
      data-testid="dialog"
      open={open}
      onClose={handleClose}
      aria-labelledby="dialog-title"
      maxWidth="sm"
      fullScreen={fullScreen}
      fullWidth
      PaperProps={{
        sx: {
          height: alertText ? INCREASED_DIALOG_HEIGHT : REGULAR_DIALOG_HEIGHT,
        },
      }}
    >
      <DialogTitle id="dialog-title">{filterDialogText.TITLE(plural)}</DialogTitle>
      <DialogContent sx={{ gap: 1.5, display: "flex", flexDirection: "column" }}>
        {dialog}
        {alertText && (
          <>
            <Divider />
            <Alert variant="standard" severity={alertSeverity}>
              {alertText}
            </Alert>
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        <div className={classes.spacer} />
        <Button color="primary" variant="text" onClick={onCancelDialog} data-testid="cancel-button">
          {globalText.CANCEL}
        </Button>
        <Button
          color="primary"
          variant="contained"
          data-testid="save-button"
          disabled={disabled || regexDisabled}
          onClick={saveFilterChangesAndClose}
        >
          {globalText.SAVE}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterDialog;
